import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import { API_BASE_URL } from '../config/constants';
import { getLanguage } from '../utils/monacoUtils';

// VS Code-style file icons with colors
const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  const name = fileName.toLowerCase();

  // Special file names
  const specialFiles = {
    'package.json': { icon: '{ }', color: '#89BB5A' },
    'package-lock.json': { icon: '{ }', color: '#89BB5A' },
    'tsconfig.json': { icon: 'TS', color: '#3178C6' },
    'jsconfig.json': { icon: 'JS', color: '#F7DF1E' },
    '.gitignore': { icon: '', color: '#F05032' },
    '.env': { icon: '⚙', color: '#ECD53F' },
    '.env.local': { icon: '⚙', color: '#ECD53F' },
    'dockerfile': { icon: '🐳', color: '#2496ED' },
    'docker-compose.yml': { icon: '🐳', color: '#2496ED' },
    'readme.md': { icon: '📖', color: '#519ABA' },
    '.eslintrc': { icon: '⬡', color: '#4B32C3' },
    '.prettierrc': { icon: '✦', color: '#F7B93E' },
    'yarn.lock': { icon: '🧶', color: '#2C8EBB' },
  };

  if (specialFiles[name]) return specialFiles[name];

  // Extensions
  const extIcons = {
    // JavaScript/TypeScript
    'js': { icon: 'JS', color: '#F7DF1E' },
    'jsx': { icon: 'JS', color: '#00D8FF' },
    'ts': { icon: 'TS', color: '#3178C6' },
    'tsx': { icon: 'TS', color: '#00D8FF' },
    'mjs': { icon: 'JS', color: '#F7DF1E' },
    'cjs': { icon: 'JS', color: '#F7DF1E' },

    // Web
    'html': { icon: '<>', color: '#E34F26' },
    'htm': { icon: '<>', color: '#E34F26' },
    'css': { icon: '#', color: '#1572B6' },
    'scss': { icon: '#', color: '#CC6699' },
    'sass': { icon: '#', color: '#CC6699' },
    'less': { icon: '#', color: '#1D365D' },
    'vue': { icon: 'V', color: '#4FC08D' },
    'svelte': { icon: 'S', color: '#FF3E00' },

    // Data/Config
    'json': { icon: '{ }', color: '#F7DF1E' },
    'yaml': { icon: '⚙', color: '#CB171E' },
    'yml': { icon: '⚙', color: '#CB171E' },
    'xml': { icon: '<>', color: '#F80' },
    'toml': { icon: '⚙', color: '#9C4121' },

    // Documentation
    'md': { icon: 'M↓', color: '#519ABA' },
    'mdx': { icon: 'M↓', color: '#F9AC00' },
    'txt': { icon: '☰', color: '#89898A' },

    // Images
    'svg': { icon: '◐', color: '#FFB13B' },
    'png': { icon: '🖼', color: '#89898A' },
    'jpg': { icon: '🖼', color: '#89898A' },
    'jpeg': { icon: '🖼', color: '#89898A' },
    'gif': { icon: '🖼', color: '#89898A' },
    'ico': { icon: '🖼', color: '#89898A' },
    'webp': { icon: '🖼', color: '#89898A' },

    // Programming languages
    'py': { icon: '🐍', color: '#3776AB' },
    'rb': { icon: '💎', color: '#CC342D' },
    'go': { icon: 'Go', color: '#00ADD8' },
    'rs': { icon: '🦀', color: '#DEA584' },
    'java': { icon: '☕', color: '#007396' },
    'kt': { icon: 'K', color: '#7F52FF' },
    'swift': { icon: '🐦', color: '#F05138' },
    'c': { icon: 'C', color: '#A8B9CC' },
    'cpp': { icon: 'C+', color: '#00599C' },
    'h': { icon: 'H', color: '#A8B9CC' },
    'cs': { icon: 'C#', color: '#239120' },
    'php': { icon: '🐘', color: '#777BB4' },

    // Shell/Scripts
    'sh': { icon: '$_', color: '#89E051' },
    'bash': { icon: '$_', color: '#89E051' },
    'zsh': { icon: '$_', color: '#89E051' },
    'fish': { icon: '$_', color: '#89E051' },
    'ps1': { icon: '>_', color: '#012456' },

    // Database
    'sql': { icon: '🗃', color: '#F29111' },
    'db': { icon: '🗃', color: '#003B57' },

    // Other
    'lock': { icon: '🔒', color: '#89898A' },
    'log': { icon: '📋', color: '#89898A' },
    'env': { icon: '⚙', color: '#ECD53F' },
  };

  return extIcons[ext] || { icon: '📄', color: '#89898A' };
};

// Folder icon colors based on name
const getFolderIcon = (folderName, isExpanded) => {
  const name = folderName.toLowerCase();

  const folderColors = {
    'src': '#42A5F5',
    'source': '#42A5F5',
    'lib': '#7E57C2',
    'components': '#26A69A',
    'pages': '#66BB6A',
    'views': '#66BB6A',
    'utils': '#FFA726',
    'helpers': '#FFA726',
    'hooks': '#EC407A',
    'services': '#AB47BC',
    'api': '#EF5350',
    'styles': '#EC407A',
    'css': '#EC407A',
    'assets': '#8D6E63',
    'images': '#8D6E63',
    'public': '#78909C',
    'static': '#78909C',
    'config': '#FFCA28',
    'test': '#66BB6A',
    'tests': '#66BB6A',
    '__tests__': '#66BB6A',
    'spec': '#66BB6A',
    'node_modules': '#8BC34A',
    'dist': '#90A4AE',
    'build': '#90A4AE',
    '.git': '#F05032',
    '.github': '#333',
    'docs': '#42A5F5',
    'types': '#3178C6',
  };

  const color = folderColors[name] || '#90A4AE';

  return { color, isExpanded };
};

// Dark mode file browser item with VS Code styling
function VSCodeFileBrowserItem({ item, level = 0, onFileClick, expandedFolders, setExpandedFolders, selectedPath }) {
  const isFolder = item.type === 'folder';
  const hasChildren = isFolder && item.children && item.children.length > 0;
  const isExpanded = expandedFolders.has(item.path);
  const isSelected = selectedPath === item.path;

  const handleClick = () => {
    if (isFolder && hasChildren) {
      setExpandedFolders(prev => {
        const next = new Set(prev);
        if (next.has(item.path)) {
          next.delete(item.path);
        } else {
          next.add(item.path);
        }
        return next;
      });
    } else if (!isFolder && onFileClick) {
      onFileClick(item);
    }
  };

  const indent = level * 16;
  const fileIcon = !isFolder ? getFileIcon(item.name) : null;
  const folderIcon = isFolder ? getFolderIcon(item.name, isExpanded) : null;

  return (
    <div>
      <div
        onClick={handleClick}
        style={{
          padding: '2px 8px',
          paddingLeft: `${12 + indent}px`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          fontSize: '13px',
          color: '#cccccc',
          userSelect: 'none',
          background: isSelected ? '#094771' : 'transparent',
        }}
        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#2a2d2e'; }}
        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        {/* Chevron for folders */}
        <span style={{
          width: '16px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginRight: '2px'
        }}>
          {isFolder && hasChildren && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="currentColor"
              style={{
                color: '#858585',
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.1s ease'
              }}
            >
              <path d="M6 4l4 4-4 4V4z"/>
            </svg>
          )}
        </span>

        {/* Icon - only for files */}
        {!isFolder && (
          <span style={{
            marginRight: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '16px',
            height: '16px',
            flexShrink: 0
          }}>
            <span style={{
              fontSize: fileIcon.icon.length > 2 ? '12px' : '9px',
              fontWeight: 600,
              color: fileIcon.color,
              fontFamily: fileIcon.icon.length <= 2 ? 'monospace' : 'inherit',
              lineHeight: 1
            }}>
              {fileIcon.icon}
            </span>
          </span>
        )}

        {/* Name */}
        <span style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          {item.name}
        </span>
      </div>

      {isFolder && hasChildren && isExpanded && (
        <div>
          {item.children.map((child, index) => (
            <VSCodeFileBrowserItem
              key={`${child.path}-${index}`}
              item={child}
              level={level + 1}
              onFileClick={onFileClick}
              expandedFolders={expandedFolders}
              setExpandedFolders={setExpandedFolders}
              selectedPath={selectedPath}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// VS Code-style file browser
function VSCodeFileBrowser({ files, onFileClick, selectedPath }) {
  const [expandedFolders, setExpandedFolders] = useState(() => {
    // Auto-expand first two levels
    const expanded = new Set();
    const expandLevel = (items, level) => {
      if (level >= 2) return;
      items.forEach(item => {
        if (item.type === 'folder') {
          expanded.add(item.path);
          if (item.children) {
            expandLevel(item.children, level + 1);
          }
        }
      });
    };
    if (files) expandLevel(files, 0);
    return expanded;
  });

  if (!files || files.length === 0) {
    return (
      <div style={{ padding: '20px', color: '#6b6b6b', fontSize: '13px', textAlign: 'center' }}>
        No files found
      </div>
    );
  }

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {files.map((item, index) => (
        <VSCodeFileBrowserItem
          key={`${item.path}-${index}`}
          item={item}
          level={0}
          onFileClick={onFileClick}
          expandedFolders={expandedFolders}
          setExpandedFolders={setExpandedFolders}
          selectedPath={selectedPath}
        />
      ))}
    </div>
  );
}

function CodeViewContainer({ rootTree, initialFile, onClose }) {
  // Track open files: array of { path, content, originalContent, loading, error }
  const [openFiles, setOpenFiles] = useState([]);
  const [activeFilePath, setActiveFilePath] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(250);
  const [isResizing, setIsResizing] = useState(false);
  const editorRef = useRef(null);

  // Open initial file on mount or when initialFile changes
  useEffect(() => {
    if (initialFile) {
      openFile(initialFile);
    }
  }, [initialFile]);

  // Get active file data
  const activeFile = openFiles.find(f => f.path === activeFilePath);
  const hasUnsavedChanges = activeFile && activeFile.content !== activeFile.originalContent;

  // Fetch file content
  const fetchFileContent = async (filePath) => {
    try {
      const pathParts = filePath.split('/');
      const relativePath = pathParts.length > 1 ? pathParts.slice(1).join('/') : filePath;
      const encodedPath = encodeURIComponent(relativePath);
      const response = await fetch(`${API_BASE_URL}/read?path=${encodedPath}`, {
        method: 'GET',
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`Failed to read file: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      let fileContent = '';

      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        fileContent = data.content || data || '';
      } else {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          fileContent = data.content || data || '';
        } catch (e) {
          fileContent = text;
        }
      }

      return fileContent;
    } catch (err) {
      throw err;
    }
  };

  // Open a file (from file browser or graph)
  const openFile = async (fileOrPath) => {
    const filePath = typeof fileOrPath === 'string' ? fileOrPath : fileOrPath.path;

    // Check if already open
    const existing = openFiles.find(f => f.path === filePath);
    if (existing) {
      setActiveFilePath(filePath);
      return;
    }

    // Add file with loading state
    setOpenFiles(prev => [...prev, {
      path: filePath,
      content: '',
      originalContent: '',
      loading: true,
      error: null
    }]);
    setActiveFilePath(filePath);

    // Fetch content
    try {
      const content = await fetchFileContent(filePath);
      setOpenFiles(prev => prev.map(f =>
        f.path === filePath
          ? { ...f, content, originalContent: content, loading: false }
          : f
      ));
    } catch (err) {
      setOpenFiles(prev => prev.map(f =>
        f.path === filePath
          ? { ...f, loading: false, error: err.message }
          : f
      ));
    }
  };

  // Close a file tab
  const closeFile = (filePath, e) => {
    if (e) e.stopPropagation();

    const fileToClose = openFiles.find(f => f.path === filePath);
    if (fileToClose && fileToClose.content !== fileToClose.originalContent) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }

    const newOpenFiles = openFiles.filter(f => f.path !== filePath);
    setOpenFiles(newOpenFiles);

    // If closing active file, switch to another
    if (activeFilePath === filePath) {
      if (newOpenFiles.length > 0) {
        const closedIndex = openFiles.findIndex(f => f.path === filePath);
        const newIndex = Math.min(closedIndex, newOpenFiles.length - 1);
        setActiveFilePath(newOpenFiles[newIndex].path);
      } else {
        setActiveFilePath(null);
      }
    }
  };

  // Save active file
  const saveFile = async () => {
    if (!activeFile || !hasUnsavedChanges) return;

    setSaving(true);
    setSaveStatus(null);

    try {
      const pathParts = activeFile.path.split('/');
      const relativePath = pathParts.length > 1 ? pathParts.slice(1).join('/') : activeFile.path;

      const response = await fetch(`${API_BASE_URL}/write`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({ path: relativePath, content: activeFile.content }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save file: ${response.status} ${response.statusText}`);
      }

      // Update original content to match saved content
      setOpenFiles(prev => prev.map(f =>
        f.path === activeFile.path
          ? { ...f, originalContent: f.content }
          : f
      ));
      setSaveStatus({ type: 'success', message: 'Saved!' });
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      setSaveStatus({ type: 'error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Handle editor content change
  const handleEditorChange = (value) => {
    if (!activeFilePath) return;
    setOpenFiles(prev => prev.map(f =>
      f.path === activeFilePath
        ? { ...f, content: value || '' }
        : f
    ));
  };

  // Handle editor mount
  const handleEditorMount = (editor, monaco) => {
    editorRef.current = editor;
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      saveFile();
    });
    editor.focus();
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (hasUnsavedChanges && !saving) {
          saveFile();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasUnsavedChanges, saving, activeFile]);

  // Handle sidebar resize
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = Math.max(150, Math.min(400, e.clientX));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const fileName = activeFilePath ? activeFilePath.split('/').pop() : '';
  const language = getLanguage(activeFilePath);

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      background: '#1e1e1e',
      position: 'relative'
    }}>
      {/* File Browser Sidebar - COMMENTED OUT
      <div style={{
        width: sidebarWidth,
        height: '100%',
        background: '#252526',
        borderRight: '1px solid #3c3c3c',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0
      }}>
        <div style={{
          padding: '10px 12px',
          borderBottom: '1px solid #3c3c3c',
          fontSize: '11px',
          fontWeight: '600',
          color: '#bbbbbb',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Explorer
        </div>
        <div style={{
          flex: 1,
          overflow: 'auto',
          paddingTop: '4px'
        }}>
          <VSCodeFileBrowser
            files={rootTree}
            onFileClick={openFile}
            selectedPath={activeFilePath}
          />
        </div>
      </div>

      <div
        onMouseDown={() => setIsResizing(true)}
        style={{
          width: '4px',
          cursor: 'col-resize',
          background: isResizing ? '#007acc' : 'transparent',
          transition: 'background 0.15s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = '#007acc'}
        onMouseLeave={(e) => { if (!isResizing) e.currentTarget.style.background = 'transparent'; }}
      />
      */}

      {/* Main Editor Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0
      }}>
        {/* Tab Bar */}
        <div style={{
          height: '35px',
          background: '#252526',
          borderBottom: '1px solid #3c3c3c',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden'
        }}>
          {/* Back Button */}
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '6px 10px',
              color: '#bbbbbb',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              borderRight: '1px solid #3c3c3c',
              height: '100%'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#3c3c3c'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            title="Back to Graph"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '14px', height: '14px' }}>
              <path fillRule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Tabs */}
          <div style={{
            display: 'flex',
            flex: 1,
            overflow: 'auto',
            height: '100%'
          }}>
            {openFiles.map((file) => {
              const isActive = file.path === activeFilePath;
              const hasChanges = file.content !== file.originalContent;
              const tabFileName = file.path.split('/').pop();

              return (
                <div
                  key={file.path}
                  onClick={() => setActiveFilePath(file.path)}
                  style={{
                    padding: '0 12px',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    background: isActive ? '#1e1e1e' : 'transparent',
                    borderRight: '1px solid #3c3c3c',
                    borderTop: isActive ? '1px solid #007acc' : '1px solid transparent',
                    fontSize: '13px',
                    color: isActive ? '#ffffff' : '#969696',
                    whiteSpace: 'nowrap',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = '#2d2d2d';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {tabFileName}
                    {hasChanges && (
                      <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#f59e0b',
                        flexShrink: 0
                      }} />
                    )}
                  </span>
                  <button
                    onClick={(e) => closeFile(file.path, e)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '2px',
                      color: '#969696',
                      fontSize: '14px',
                      lineHeight: 1,
                      borderRadius: '3px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#4a4a4a';
                      e.currentTarget.style.color = '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#969696';
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>

          {/* Save Button */}
          {activeFile && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '0 12px',
              borderLeft: '1px solid #3c3c3c',
              height: '100%'
            }}>
              {saving ? (
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>Saving...</span>
              ) : saveStatus ? (
                <span style={{ fontSize: '11px', color: saveStatus.type === 'success' ? '#22c55e' : '#ef4444' }}>
                  {saveStatus.message}
                </span>
              ) : null}
              <button
                onClick={saveFile}
                disabled={!hasUnsavedChanges || saving}
                style={{
                  background: hasUnsavedChanges ? '#22c55e' : '#4a4a4a',
                  color: hasUnsavedChanges ? '#fff' : '#888',
                  border: 'none',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: hasUnsavedChanges && !saving ? 'pointer' : 'default',
                  opacity: hasUnsavedChanges ? 1 : 0.6
                }}
              >
                Save
              </button>
            </div>
          )}
        </div>

        {/* Editor Content */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {!activeFile ? (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b6b6b'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>📄</div>
              <div style={{ fontSize: '14px' }}>Select a file to view</div>
            </div>
          ) : activeFile.loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#9ca3af',
              fontSize: '14px'
            }}>
              Loading file...
            </div>
          ) : activeFile.error ? (
            <div style={{
              padding: '20px',
              background: '#3c1f1f',
              color: '#ef4444',
              borderRadius: '6px',
              fontSize: '13px',
              margin: '20px'
            }}>
              Error: {activeFile.error}
            </div>
          ) : (
            <Editor
              height="100%"
              language={language}
              value={activeFile.content}
              theme="vs-dark"
              onMount={handleEditorMount}
              onChange={handleEditorChange}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                wordWrap: 'off',
                automaticLayout: true,
                tabSize: 2,
                folding: true,
                lineNumbersMinChars: 4,
                padding: { top: 16 }
              }}
            />
          )}
        </div>

        {/* Status Bar */}
        {activeFile && !activeFile.loading && !activeFile.error && (
          <div style={{
            height: '22px',
            background: '#007acc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 12px',
            fontSize: '12px',
            color: '#ffffff'
          }}>
            <span style={{ fontFamily: 'monospace' }}>{activeFile.path}</span>
            <span>{activeFile.content.split('\n').length} lines • {language}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default CodeViewContainer;
